import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, TrendingUp, TrendingDown, AlertTriangle, Search,
  Download, Building2, CheckCircle2, DollarSign
} from 'lucide-react';
import api from '../../services/api';
import { formatCurrency } from '../../utils/format';
import FinancialHeader from './FinancialHeader';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

export default function ContractProfitability() {
  const [ownerCompanyId, setOwnerCompanyId] = useState('all');
  const [ownerCompanies, setOwnerCompanies] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Load companies
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/owner-companies');
        setOwnerCompanies(res.data?.data || res.data || []);
      } catch (err) {
        console.error('Erro ao buscar empresas', err);
      }
    })();
  }, []);

  const loadProfitability = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (ownerCompanyId && ownerCompanyId !== 'all') params.owner_company_id = ownerCompanyId;

      const res = await api.get('/financial/contract-profitability', { params });
      const raw = res.data;
      const list = raw?.data || raw?.items || (Array.isArray(raw) ? raw : []);
      setContracts(list);
    } catch (err) {
      console.error('Erro ao carregar rentabilidade dos contratos', err);
      toast.error('Erro ao carregar análise de rentabilidade');
    } finally {
      setLoading(false);
    }
  }, [ownerCompanyId]);

  useEffect(() => {
    loadProfitability();
  }, [loadProfitability]);

  const filteredContracts = contracts.filter(c => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (c.client_name || '').toLowerCase().includes(s) ||
      (c.contract_number || '').toLowerCase().includes(s)
    );
  });

  // KPIs
  const totalRevenue = filteredContracts.reduce((acc, c) => acc + (c.revenue_realized || c.revenue_projected || 0), 0);
  const totalExpenses = filteredContracts.reduce((acc, c) => acc + (c.total_expenses || 0), 0);
  const totalMargin = totalRevenue - totalExpenses;
  const avgMarginPct = totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;
  const lossContractsCount = filteredContracts.filter(c => c.net_margin < 0).length;

  const handleExportCSV = () => {
    if (!filteredContracts.length) return;
    const headers = ['Contrato', 'Cliente', 'Valor Contratual', 'Receita Faturada', 'Custos Operacionais', 'Custos Extraordinários', 'Margem Líquida (R$)', 'Margem (%)'];
    const rows = filteredContracts.map(c => [
      c.contract_number || '',
      `"${(c.client_name || '').replace(/"/g, '""')}"`,
      c.contract_value?.toFixed(2) || '0.00',
      (c.revenue_realized || c.revenue_projected || 0).toFixed(2),
      c.total_expenses?.toFixed(2) || '0.00',
      c.extraordinary_expenses?.toFixed(2) || '0.00',
      c.net_margin?.toFixed(2) || '0.00',
      `${c.margin_percent || 0}%`
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'rentabilidade_contratos.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Relatório exportado com sucesso');
  };

  return (
    <div className="space-y-6">
      <FinancialHeader
        title="Rentabilidade por Contrato"
        subtitle="Apuração de receitas faturadas vs custos operacionais vinculados por contrato"
        ownerCompanyId={ownerCompanyId}
        setOwnerCompanyId={setOwnerCompanyId}
        ownerCompanies={ownerCompanies}
        onRefresh={loadProfitability}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Receita Total de Contratos</p>
          <p className="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">
            {formatCurrency(totalRevenue)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">{filteredContracts.length} contratos ativos</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Custos Vinculados</p>
          <p className="text-2xl font-bold mt-1 text-red-600 dark:text-red-400">
            {formatCurrency(totalExpenses)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">Manutenções, combustível e diretos</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Margem Líquida Total</p>
          <p className={`text-2xl font-bold mt-1 ${totalMargin >= 0 ? 'text-neutral-900 dark:text-neutral-100' : 'text-red-600'}`}>
            {formatCurrency(totalMargin)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">Média: {avgMarginPct.toFixed(1)}%</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Contratos com Prejuízo</p>
          <p className={`text-2xl font-bold mt-1 ${lossContractsCount > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
            {lossContractsCount}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">{lossContractsCount > 0 ? 'Custos superiores à receita' : 'Todos os contratos saudáveis'}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-3">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-neutral-400" />
          <Input
            type="text"
            placeholder="Buscar por cliente, número..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-xs"
          />
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCSV}
          className="h-9 text-xs gap-1.5"
        >
          <Download className="h-3.5 w-3.5" />
          Exportar Análise
        </Button>
      </div>

      {/* Tabela de Rentabilidade */}
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-neutral-50 dark:bg-neutral-800/50 text-neutral-500 font-semibold border-b border-neutral-200 dark:border-neutral-800">
              <tr>
                <th className="py-3 px-4">Contrato</th>
                <th className="py-3 px-4">Cliente</th>
                <th className="py-3 px-4 text-right">Valor Contratual</th>
                <th className="py-3 px-4 text-right">Receita Faturada</th>
                <th className="py-3 px-4 text-right">Custos Totais</th>
                <th className="py-3 px-4 text-right">Custos Extraord.</th>
                <th className="py-3 px-4 text-right">Margem Líquida</th>
                <th className="py-3 px-4 text-center">Status Margem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={8} className="py-4 px-4">
                      <div className="h-4 bg-neutral-100 dark:bg-neutral-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : filteredContracts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-neutral-400">
                    Nenhum contrato ativo encontrado.
                  </td>
                </tr>
              ) : (
                filteredContracts.map((c) => {
                  const isNegative = c.net_margin < 0;
                  const isHigh = c.margin_percent >= 25;
                  const isLow = c.margin_percent < 10 && !isNegative;

                  return (
                    <tr key={c.contract_id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/40">
                      <td className="py-3 px-4 font-semibold text-neutral-900 dark:text-neutral-100">
                        {c.contract_number || 'S/N'}
                      </td>
                      <td className="py-3 px-4 font-medium text-neutral-800 dark:text-neutral-200">
                        🏢 {c.client_name}
                      </td>
                      <td className="py-3 px-4 text-right text-neutral-600 dark:text-neutral-300">
                        {formatCurrency(c.contract_value)}
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(c.revenue_realized || c.revenue_projected)}
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-red-600 dark:text-red-400">
                        {formatCurrency(c.total_expenses)}
                      </td>
                      <td className="py-3 px-4 text-right text-neutral-500">
                        {c.extraordinary_expenses > 0 ? (
                          <span className="text-amber-600 font-medium">
                            {formatCurrency(c.extraordinary_expenses)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className={`py-3 px-4 text-right font-bold ${isNegative ? 'text-red-600' : 'text-neutral-900 dark:text-neutral-100'}`}>
                        {formatCurrency(c.net_margin)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {isNegative ? (
                          <Badge className="bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300 border-red-200">
                            Prejuízo ({c.margin_percent}%)
                          </Badge>
                        ) : isHigh ? (
                          <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200">
                            Alta ({c.margin_percent}%)
                          </Badge>
                        ) : isLow ? (
                          <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200">
                            Atenção ({c.margin_percent}%)
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200">
                            Saudável ({c.margin_percent}%)
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
